import { RefObject, useEffect, useState } from "react";

// How far ahead of the viewport deferred media starts to load: far enough that ordinary scrolling finds it
// loaded, near enough that nothing further down the page downloads alongside the first paint.
const NEAR_VIEWPORT_ROOT_MARGIN = "600px 0px";

/**
 * False on the server and on the first client render, so hydration sees the prerendered markup unchanged;
 * true from the moment the element comes within NEAR_VIEWPORT_ROOT_MARGIN of the viewport, and from then on.
 * Media below the fold uses it to hold back its download: native `loading="lazy"` is not enough on a slow
 * phone, where Chrome starts lazy images up to 2500px below the viewport, which on the landing page is
 * most of them.
 */
export function useIsNearViewport(elementRef: RefObject<Element>): boolean {
  const [isNearViewport, setIsNearViewport] = useState(false);
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT_ROOT_MARGIN }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef]);
  return isNearViewport;
}
